import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Box,
  TableCell as MuiTableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import PersonAdd from '@mui/icons-material/PersonAdd';
import Casino from '@mui/icons-material/Casino';
import CardMembership from '@mui/icons-material/CardMembership';
import InfoIcon from '@mui/icons-material/Info';
import Title from '../../../components/Title';
import { formatDate } from '../../../resources/functions';
import TableCell from '../../../components/TableCell';
import {
  IPromoCodeType,
  Promocode
} from '../../../resources/types/balanceTypes';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../../redux/slices/authSlice';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
  },
  transition: 'background-color 0.2s ease',
  cursor: 'pointer'
}));

const TableHeaderRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: 'transparent',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px',
  '&:first-of-type': {
    paddingLeft: '20px'
  },
  '&:last-of-type': {
    paddingRight: '20px'
  }
}));

const PREFIX = 'PromocodesPage';

const classes = {
  header: `${PREFIX}-header`,
  addButton: `${PREFIX}-addButton`,
  tableContainer: `${PREFIX}-tableContainer`
};

const getPromoCodeTypeConfig = (t: TFunction, type?: IPromoCodeType) => {
  switch (type) {
    case IPromoCodeType.addBalance:
      return {
        backgroundColor: 'rgba(76, 175, 80, 0.12)',
        iconColor: '#4CAF50',
        icon: AccountBalanceWallet,
        tooltip: t('promocodes.typeAddBalance')
      };
    case IPromoCodeType.referal:
      return {
        backgroundColor: 'rgba(33, 150, 243, 0.12)',
        iconColor: '#2196F3',
        icon: PersonAdd,
        tooltip: t('promocodes.typeReferal')
      };
    case IPromoCodeType.fortuneWheel:
      return {
        backgroundColor: 'rgba(255, 152, 0, 0.12)',
        iconColor: '#FF9800',
        icon: Casino,
        tooltip: t('promocodes.typeFortuneWheel')
      };
    case IPromoCodeType.subscription:
      return {
        backgroundColor: 'rgba(156, 39, 176, 0.12)',
        iconColor: '#9C27B0',
        icon: CardMembership,
        tooltip: t('promocodes.typeSubscription')
      };
    default:
      return {
        backgroundColor: 'rgba(158, 158, 158, 0.12)',
        iconColor: '#9E9E9E',
        icon: AccountBalanceWallet,
        tooltip: t('promocodes.typeUnknown')
      };
  }
};

interface IPromoCodesListWrapperprops {
  title: string;
  createButtonTitle: string;
  onCreate: () => void;
  onEdit: (promocode: Promocode) => void;
  promocodes: Promocode[];
  includeReferal: boolean;
  setIncludeReferal: (includeReferal: boolean) => void;
}

const PromoCodesListWrapper = ({
  onCreate,
  onEdit,
  promocodes,
  title,
  createButtonTitle,
  includeReferal,
  setIncludeReferal
}: IPromoCodesListWrapperprops) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const auth = useSelector(selectAuth);
  const isSuperAdmin = auth.info?.type === 'superAdmin';
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedPromocode, setSelectedPromocode] = useState<Promocode | null>(null);

  const handleOpenDetails = (promocode: Promocode) => {
    setSelectedPromocode(promocode);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedPromocode(null);
  };

  return (
    <>
      <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Title>{title}</Title>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeReferal ?? false}
                  color='primary'
                  onChange={(event, checked) => setIncludeReferal(checked)}
                />
              }
              label={t('promocodes.addReferalCodes')}
            />
            <Button
              color='primary'
              startIcon={<AddIcon />}
              variant='contained'
              onClick={() => onCreate()}
            >
              {createButtonTitle}
            </Button>
          </Box>
        </Box>
        {promocodes.length ? (
          <div>
            <TableContainer className={classes.tableContainer} sx={{ overflow: 'hidden', borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableHeaderRow>
                    <TableHeaderCell>{t('promocodes.colType')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colCode')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colDescription')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colDiscount')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colUsageCount')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colValidUntil')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colCreatedAt')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colUpdatedAt')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colActive')}</TableHeaderCell>
                    <TableHeaderCell>{t('promocodes.colDetails')}</TableHeaderCell>
                  </TableHeaderRow>
                </TableHead>
                <TableBody>
                  {promocodes.map(promocode => {
                    const typeConfig = getPromoCodeTypeConfig(t, promocode.type);
                    const TypeIcon = typeConfig.icon;
                    
                    return (
                      <StyledTableRow
                        key={promocode._id}
                        onClick={() => {
                          if (
                            promocode.type === IPromoCodeType.addBalance &&
                            isSuperAdmin
                          ) {
                            onEdit(promocode);
                          }
                        }}
                      >
                        <TableCell>
                          <Tooltip title={typeConfig.tooltip} arrow>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: typeConfig.backgroundColor,
                                borderRadius: '8px'
                              }}
                            >
                              <TypeIcon sx={{ fontSize: 24, color: typeConfig.iconColor }} />
                            </Avatar>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{promocode.code}</TableCell>
                        <TableCell>{promocode.description}</TableCell>
                        <TableCell>{promocode.amount}</TableCell>
                        <TableCell>
                          {promocode.type === IPromoCodeType.addBalance
                            ? `${promocode.usedTimes || 0} / ${
                                promocode.usingLimit || 0
                              }`
                            : promocode.usedTimes}
                        </TableCell>
                        <TableCell>
                          {promocode.expiresAt
                            ? moment(promocode.expiresAt).format('DD.MM.YYYY')
                            : ''}
                        </TableCell>
                        <TableCell>{formatDate(promocode.createdAt)}</TableCell>
                        <TableCell>{formatDate(promocode.updatedAt)}</TableCell>
                        <TableCell>
                          <Checkbox
                            disableRipple
                            readOnly
                            checked={promocode.active}
                            color='primary'
                          />
                        </TableCell>
                        <TableCell>
                          {(promocode.type === IPromoCodeType.referal || 
                            promocode.type === IPromoCodeType.fortuneWheel) && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDetails(promocode);
                              }}
                              sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'light' 
                                    ? 'rgba(0, 0, 0, 0.04)' 
                                    : 'rgba(255, 255, 255, 0.08)'
                                }
                              }}
                            >
                              <InfoIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        ) : null}
      </Paper>

      {/* Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="h6">
              {t('promocodes.detailsTitle', { code: selectedPromocode?.code })}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPromocode && (
            <Box>
              {/* Owner/Creator info for referal codes */}
              {selectedPromocode.type === IPromoCodeType.referal && selectedPromocode.user && (
                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('promocodes.ownerLabel')}
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A' }}>
                    <Typography variant="body1">
                      <strong>{t('promocodes.phoneLabel')}</strong> {selectedPromocode.user.phone || t('promocodes.notSpecified')}
                    </Typography>
                    {selectedPromocode.user.name && (
                      <Typography variant="body1">
                        <strong>{t('promocodes.nameLabel')}</strong> {selectedPromocode.user.name}
                      </Typography>
                    )}
                    {selectedPromocode.user.email && (
                      <Typography variant="body1">
                        <strong>Email:</strong> {selectedPromocode.user.email}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Winner info for fortune wheel */}
              {selectedPromocode.type === IPromoCodeType.fortuneWheel && selectedPromocode.user && (
                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('promocodes.winnerLabel')}
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A' }}>
                    <Typography variant="body1">
                      <strong>{t('promocodes.phoneLabel')}</strong> {selectedPromocode.user.phone || t('promocodes.notSpecified')}
                    </Typography>
                    {selectedPromocode.user.name && (
                      <Typography variant="body1">
                        <strong>{t('promocodes.nameLabel')}</strong> {selectedPromocode.user.name}
                      </Typography>
                    )}
                    {selectedPromocode.user.email && (
                      <Typography variant="body1">
                        <strong>Email:</strong> {selectedPromocode.user.email}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Activated by info */}
              {selectedPromocode.activatedBy && (
                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('promocodes.activatedByLabel')}
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: theme.palette.mode === 'light' ? '#E8F5E9' : '#1B5E20' }}>
                    <Typography variant="body1">
                      <strong>{t('promocodes.phoneLabel')}</strong> {selectedPromocode.activatedBy.phone || t('promocodes.notSpecified')}
                    </Typography>
                    {selectedPromocode.activatedBy.name && (
                      <Typography variant="body1">
                        <strong>{t('promocodes.nameLabel')}</strong> {selectedPromocode.activatedBy.name}
                      </Typography>
                    )}
                    {selectedPromocode.activatedBy.email && (
                      <Typography variant="body1">
                        <strong>Email:</strong> {selectedPromocode.activatedBy.email}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Used by list */}
              {selectedPromocode.usedBy && selectedPromocode.usedBy.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('promocodes.usedByLabel', { count: selectedPromocode.usedBy.length })}
                  </Typography>
                  <List sx={{ 
                    maxHeight: 300, 
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1
                  }}>
                    {selectedPromocode.usedBy.map((user, index) => (
                      <ListItem 
                        key={user._id || index}
                        sx={{
                          borderBottom: index < selectedPromocode.usedBy!.length - 1 
                            ? `1px solid ${theme.palette.divider}` 
                            : 'none'
                        }}
                      >
                        <ListItemText
                          primary={user.phone || t('promocodes.noPhone')}
                          secondary={
                            <>
                              {user.name && <span>{user.name}</span>}
                              {user.email && <span> • {user.email}</span>}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* No data message */}
              {!selectedPromocode.user && 
               !selectedPromocode.activatedBy && 
               (!selectedPromocode.usedBy || selectedPromocode.usedBy.length === 0) && (
                <Typography color="textSecondary" align="center">
                  {t('promocodes.noUserData')}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails} color="primary" variant="contained">
            {t('promocodes.closeBtn')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PromoCodesListWrapper;
